import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, Music2, Clock, Disc3 } from "lucide-react";
import api from "../lib/api";
import MinistryGrid from "../components/MinistryGrid";
import MarqueeText from "../components/MarqueeText";
import { RADIO_MINISTRIES } from "../lib/ministries";
import { LINK_RADIO_YT } from "../lib/siteConfig";

const RADIO_STATION_ID = "s31b752d31";
const STREAM_URL = `https://s3.radio.co/${RADIO_STATION_ID}/listen`;
const STATUS_URL = `https://public.radio.co/stations/${RADIO_STATION_ID}/status`;
const STATUS_POLL_MS = 15_000;

/**
 * Polls Radio.co's public status endpoint every 15 seconds for the
 * currently-airing track / podcast episode and its artwork.
 * Returns { title, artwork, status } — values stay `null`/empty on error.
 */
function useNowPlaying() {
  const [meta, setMeta] = useState({ title: "", artwork: "", status: "" });

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const fetchStatus = async () => {
      try {
        const res = await fetch(STATUS_URL, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const track = data?.current_track || {};
        setMeta({
          title: typeof track.title === "string" ? track.title.trim() : "",
          artwork: typeof track.artwork_url === "string" ? track.artwork_url : "",
          status: data?.status || "",
        });
      } catch {
        // Silent — keep last good metadata if available.
      } finally {
        if (!cancelled) {
          timer = window.setTimeout(fetchStatus, STATUS_POLL_MS);
        }
      }
    };

    fetchStatus();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  return meta;
}

export default function ListenLive() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState("");
  const nowPlaying = useNowPlaying();

  // Sync volume to <audio>
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = muted;
    }
  }, [volume, muted]);

  const handleToggle = async () => {
    const el = audioRef.current;
    if (!el) return;
    setError("");
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    try {
      setLoading(true);
      // Force browser to bypass cache and pull the LIVE stream moment
      el.load();
      el.src = STREAM_URL;
      el.load();
      await el.play();
      setPlaying(true);
    } catch (e) {
      setError(
        "Unable to start the live stream. Please check your connection or try a different browser."
      );
      setPlaying(false);
    } finally {
      setLoading(false);
    }
  };

  const onAudioEvent = (type) => () => {
    if (type === "ended" || type === "pause") setPlaying(false);
    if (type === "playing") {
      setPlaying(true);
      setLoading(false);
    }
    if (type === "waiting") setLoading(true);
    if (type === "error") {
      setError("Stream interrupted. Please reconnect.");
      setPlaying(false);
      setLoading(false);
    }
  };

  const valPct = Math.round((muted ? 0 : volume) * 100);

  return (
    <main data-testid="listen-page" className="bg-canvas">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 text-[10px] tracking-[0.25em] uppercase">
          <span className="live-dot" /> Live broadcast
        </div>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
          Life Talk Radio Station
        </h1>
        <p className="mt-4 text-[#C5D1EB] max-w-xl leading-relaxed">
          A distraction-free room for the 24/7 signal. Hit play — the player
          flushes any cached audio and pulls the exact live moment.
        </p>

        {/* Song request CTA */}
       <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/contact#song"
          data-testid="song-request-cta"
          className="inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-5 py-3 text-sm transition-colors"
        >
          <Music2 size={14} /> Make a Song Request
        </Link>

        <a
          href={LINK_RADIO_YT}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 px-5 py-3 text-sm transition-colors"
        >
          Open Life talk radio youtube channel
        </a>
      </div>

        {/* Player card with ambient crimson glow */}
        <section
          data-testid="live-player"
          className="relative mt-10 bg-white/[0.04] backdrop-blur-md border border-white/10 p-6 sm:p-10 overflow-hidden"
        >
          {/* Ambient studio glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full opacity-40 blur-[120px]"
            style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-32 -left-32 w-[24rem] h-[24rem] rounded-full opacity-30 blur-[120px]"
            style={{ background: "radial-gradient(circle, #E31C3D, transparent 70%)" }}
          />

          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5D1EB]/70">
                Now Streaming
              </p>
              <p className="mt-2 font-display text-2xl sm:text-3xl text-white">
                Life Talk Radio · 24/7
              </p>
              <p className="mt-1 text-xs text-[#C5D1EB]/80">
                Indian & multi-ethnic gospel · Tauranga, NZ
              </p>
            </div>
            <span
              data-testid="live-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/35 text-[11px] tracking-[0.2em] uppercase"
            >
              <span className="live-dot" /> On Air
            </span>
          </div>

          <div className="relative mt-10 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7">
            <button
              data-testid="play-toggle"
              onClick={handleToggle}
              aria-label={playing ? "Pause live stream" : "Play live stream"}
              className="shrink-0 flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-[#C5001A] hover:bg-[#E31C3D] text-white shadow-[0_8px_40px_rgba(197,0,26,0.45)] transition-colors disabled:opacity-60"
              disabled={loading}
            >
              {loading ? (
                <span
                  className="eq-bars eq-bars-light"
                  data-testid="player-loading"
                  style={{ height: "28px" }}
                >
                  <span /><span /><span /><span />
                </span>
              ) : playing ? (
                <Pause size={30} fill="currentColor" />
              ) : (
                <Play size={30} fill="currentColor" />
              )}
            </button>

            {/* Now Playing metadata — Radio.co status */}
            <NowPlayingCard meta={nowPlaying} />

            <div className="flex-1 min-w-0 sm:max-w-[280px]">
              <div className="flex items-center justify-between">
                <span className="text-[11px] tracking-[0.25em] uppercase text-[#C5D1EB]/70">
                  Volume
                </span>
                <span className="text-xs text-[#C5D1EB]">{valPct}%</span>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <button
                  data-testid="mute-toggle"
                  onClick={() => setMuted((v) => !v)}
                  className="text-[#C5D1EB] hover:text-white"
                  aria-label={muted ? "Unmute" : "Mute"}
                >
                  {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <input
                  data-testid="volume-slider"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={muted ? 0 : volume}
                  onChange={(e) => {
                    setMuted(false);
                    setVolume(parseFloat(e.target.value));
                  }}
                  className="brand-range"
                  style={{ "--val": `${valPct}%` }}
                />
              </div>
            </div>
          </div>

          {error && (
            <p data-testid="player-error" className="mt-6 text-sm text-[#E31C3D]">
              {error}
            </p>
          )}

          <audio
            ref={audioRef}
            preload="none"
            onLoadStart={onAudioEvent("waiting")}
            onCanPlay={onAudioEvent("playing")}
            onPlaying={onAudioEvent("playing")}
            onPause={onAudioEvent("pause")}
            onEnded={onAudioEvent("ended")}
            onWaiting={onAudioEvent("waiting")}
            onError={onAudioEvent("error")}
            data-testid="audio-element"
          />
        </section>

        {/* Studio notes */}
        <section className="mt-12 grid sm:grid-cols-3 gap-5">
          {[
            ["Bitrate", "128 kbps", "AAC stereo over HTTPS"],
            ["Format", "Live HLS / MP3", "Compatible with all browsers"],
            ["Source", "Radio.co", "Resilient global delivery"],
          ].map(([k, v, hint]) => (
            <div
              key={k}
              className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-5"
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5D1EB]/70">
                {k}
              </p>
              <p className="mt-2 font-display text-white text-xl">{v}</p>
              <p className="mt-1 text-xs text-[#C5D1EB]/70">{hint}</p>
            </div>
          ))}
        </section>
      </div>

      {/* 24/7 broadcast schedule */}
      <BroadcastSchedule />

      {/* Life Talk Radio ministries */}
      <MinistryGrid
        items={RADIO_MINISTRIES}
        heading="Life Talk Radio Ministries"
        subheading="Six expressions of one Gospel — carried over FM, online, and in person."
        testId="radio-ministries"
      />
    </main>
  );
}

function BroadcastSchedule() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/schedule")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Sort strictly by start_time (chronological, hourly blocks).
  const sorted = [...items].sort((a, b) =>
    (a.start_time || "").localeCompare(b.start_time || "")
  );

  return (
    <section
      data-testid="broadcast-schedule"
      className="relative overflow-hidden border-t border-white/5"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 right-1/4 w-[40rem] h-[40rem] rounded-full opacity-20 blur-[120px]"
        style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
              On Air · Time Slots
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-3 tracking-tight max-w-2xl leading-[1.05]">
              24 / 7 Broadcast Schedule
            </h2>
          </div>
          <p className="text-[#C5D1EB] text-sm sm:text-base max-w-md leading-relaxed">
            A continuous, time-slotted rotation of worship, prayer, and Word —
            broadcasting across New Zealand round-the-clock.
          </p>
        </div>

        {loading ? (
          <p className="text-[#C5D1EB] text-sm">Loading schedule…</p>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-[#C5D1EB]/70 bg-white/[0.03] backdrop-blur-md border border-white/10 p-6">
            The on-air lineup will appear here once the studio team publishes
            it from the admin dashboard.
          </p>
        ) : (
          <ol
            data-testid="schedule-timeline"
            className="divide-y divide-white/10 border border-white/10 bg-white/[0.02] backdrop-blur-md"
          >
            {sorted.map((s) => (
              <li
                key={s.id}
                data-testid={`schedule-slot-${s.id}`}
                className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3 sm:gap-6 px-5 sm:px-7 py-5 hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Clock size={15} className="text-[#C5001A] shrink-0" />
                  <span className="font-display text-white text-base sm:text-lg tabular-nums tracking-tight">
                    {s.start_time}
                    <span className="text-[#C5D1EB]/50"> – </span>
                    {s.end_time}
                  </span>
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h3 className="font-display text-white text-base sm:text-lg leading-tight">
                      {s.title}
                    </h3>
                    {s.host && (
                      <span className="text-xs sm:text-sm text-[#C5001A]">
                        · {s.host}
                      </span>
                    )}
                    {s.day && (
                      <span className="text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/60">
                        {s.day}
                      </span>
                    )}
                  </div>
                  {s.description && (
                    <p className="mt-1.5 text-sm text-[#C5D1EB]/85 leading-relaxed">
                      {s.description}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}


/* -------------------------------------------------------------------------- */
/*  Now Playing metadata card — Radio.co live track / podcast episode         */
/* -------------------------------------------------------------------------- */
function NowPlayingCard({ meta }) {
  const { title, artwork } = meta || {};
  const hasTitle = !!title;

  return (
    <div
      data-testid="now-playing-card"
      aria-live="polite"
      className="relative flex-1 min-w-0 flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2.5 bg-white/[0.05] backdrop-blur-md border border-white/10 overflow-hidden"
    >
      {/* Soft red ambient wash so the card feels alive */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-12 -top-12 w-40 h-40 rounded-full opacity-25 blur-[60px]"
        style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
      />

      {/* Artwork micro-thumbnail (or fallback) */}
      <div
        data-testid="now-playing-artwork"
        className="relative shrink-0 w-12 h-12 sm:w-14 sm:h-14 bg-[#0B0C10] border border-white/10 overflow-hidden flex items-center justify-center"
      >
        {artwork ? (
          <img
            src={artwork}
            alt={hasTitle ? title : "Now playing artwork"}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <Disc3
            size={20}
            className="text-[#C5001A] animate-spin"
            style={{ animationDuration: "6s" }}
            aria-hidden
          />
        )}
      </div>

      {/* Title block */}
      <div className="relative min-w-0 flex-1">
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#FF3B30] flex items-center gap-1.5">
          <span className="live-dot" /> Now Playing
        </p>
        <MarqueeText
          testid="now-playing-title"
          text={hasTitle ? title : "Streaming live · awaiting track info"}
          className="mt-1 font-display text-white text-sm sm:text-base tracking-tight"
        />
      </div>
    </div>
  );
}
